/**
 * 통계 대시보드 메인 페이지
 * 학생별/문제집별 통계 선택 화면
 */

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Button, Select, Space, message } from 'antd';
import { UserOutlined, LineChartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';

const { Title, Text } = Typography;

interface User {
  id: string;
  name: string;
  email: string;
}

interface Workbook {
  name: string;
  year: number;
  grade: number;
  semester: number;
}

export const StatsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState<User[]>([]);
  const [workbooks, setWorkbooks] = useState<Workbook[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string | undefined>(undefined);
  const [selectedWorkbook, setSelectedWorkbook] = useState<string | undefined>(undefined);
  const [loadingWorkbooks, setLoadingWorkbooks] = useState(false);

  // 학생 목록 로드
  useEffect(() => {
    const loadStudents = async () => {
      try {
        const response = await api.get('/users', {
          params: { role: 'student', page_size: 100 },
        });
        // UserListResponse 형식: { users: [], total, page, page_size, total_pages }
        setStudents(response.data?.users || []);
      } catch (error: any) {
        console.error('학생 목록 로드 실패:', error);
      }
    };

    loadStudents();
  }, []);

  // 학생이 선택되면 해당 학생에게 할당된 문제집 로드
  useEffect(() => {
    const loadStudentWorkbooks = async () => {
      if (!selectedStudent) {
        setWorkbooks([]);
        return;
      }

      setLoadingWorkbooks(true);
      try {
        const response = await api.get(`/assignments/students/${selectedStudent}`);
        // student_workbooks 데이터에서 workbook_name 추출
        const assignments = response.data?.assignments || [];

        // 문제집 상세 정보를 가져오기 위해 workbook_name으로 조회
        const workbookNamesSet = new Set<string>(assignments.map((a: any) => a.workbook_name));
        const workbookNames: string[] = Array.from(workbookNamesSet);
        const workbookPromises = workbookNames.map(async (name: string) => {
          try {
            const wb = await api.get(`/workbooks/${encodeURIComponent(name)}`);
            return wb.data?.workbook || null;
          } catch {
            // 문제집 정보를 가져오지 못하면 이름만 반환
            return { name };
          }
        });

        const loadedWorkbooks = await Promise.all(workbookPromises);
        setWorkbooks(loadedWorkbooks.filter(wb => wb !== null));
      } catch (error: any) {
        console.error('학생 문제집 로드 실패:', error);
        message.error('학생에게 할당된 문제집을 불러오는데 실패했습니다');
        setWorkbooks([]);
      } finally {
        setLoadingWorkbooks(false);
      }
    };

    loadStudentWorkbooks();
    // 학생이 변경되면 선택된 문제집 초기화
    setSelectedWorkbook(undefined);
  }, [selectedStudent]);

  // 학생별 통계 보기
  const handleViewStudentStats = () => {
    if (!selectedStudent) {
      message.warning('학생을 선택해주세요');
      return;
    }
    navigate(`/stats/students/${selectedStudent}`);
  };

  // 학생 진도 추이 보기
  const handleViewProgress = () => {
    if (!selectedStudent) {
      message.warning('학생을 선택해주세요');
      return;
    }
    if (!selectedWorkbook) {
      message.warning('문제집을 선택해주세요');
      return;
    }
    navigate(`/stats/students/${selectedStudent}/workbooks/${selectedWorkbook}/progress`);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Title level={2}>통계 및 분석</Title>
        <Text type="secondary">
          학생별 성적과 학습 진도 추이를 확인할 수 있습니다.
        </Text>
      </Card>

      <Row gutter={[16, 16]}>
        {/* 학생별 통계 */}
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                학생별 통계
              </Space>
            }
            hoverable
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>학생의 전체 정답률, 시도별 통계, 답형식별 성적을 확인합니다.</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="학생 선택"
                value={selectedStudent}
                onChange={setSelectedStudent}
                showSearch
                optionFilterProp="children"
              >
                {students.map((student) => (
                  <Select.Option key={student.id} value={student.id}>
                    {student.name} ({student.email})
                  </Select.Option>
                ))}
              </Select>
              <Button
                type="primary"
                block
                onClick={handleViewStudentStats}
                disabled={!selectedStudent}
              >
                통계 보기
              </Button>
            </Space>
          </Card>
        </Col>

        {/* 학습 진도 추이 */}
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <LineChartOutlined />
                학습 진도 추이
              </Space>
            }
            hoverable
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>특정 학생의 문제집 학습 진도를 시간별로 확인합니다.</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="학생 선택"
                value={selectedStudent}
                onChange={setSelectedStudent}
                showSearch
                optionFilterProp="children"
              >
                {students.map((student) => (
                  <Select.Option key={student.id} value={student.id}>
                    {student.name}
                  </Select.Option>
                ))}
              </Select>
              <Select
                style={{ width: '100%' }}
                placeholder="문제집 선택"
                value={selectedWorkbook}
                onChange={setSelectedWorkbook}
                showSearch
                optionFilterProp="children"
                disabled={!selectedStudent}
                loading={loadingWorkbooks}
                notFoundContent={selectedStudent ? '할당된 문제집이 없습니다' : '먼저 학생을 선택해주세요'}
              >
                {workbooks.map((workbook) => (
                  <Select.Option key={workbook.name} value={workbook.name}>
                    {workbook.name}
                  </Select.Option>
                ))}
              </Select>
              <Button
                type="primary"
                block
                onClick={handleViewProgress}
                disabled={!selectedStudent || !selectedWorkbook}
              >
                진도 추이 보기
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default StatsDashboard;
